import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup";
import { UserGroupModel } from "services/api/cplauthapi/CPLAuthAPI";
import { container } from "setup/dependency/inversify.config";
import { IUserGroupsService } from "services/UserGroupsService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";

type UserGroupState = {
  UserGroups: Array<UserGroupModel> | undefined;
  UserGroup: UserGroupModel | undefined;
  FetchingUserGroups: boolean;
  CreatingUserGroup: boolean;
  DeletingUserGroup: boolean;
  UpdatingUserGroup: boolean;
};

const initialState: UserGroupState = {
  UserGroups: undefined,
  UserGroup: undefined,
  FetchingUserGroups: false,
  CreatingUserGroup: false,
  DeletingUserGroup: false,
  UpdatingUserGroup: false,
};

export const userGroupSlice = createSlice({
  name: "userGroups",
  initialState,
  reducers: {
    resetUserGroup: (state) => {
      state.UserGroup = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUserGroups.pending, (state) => {
        state.FetchingUserGroups = true;
      })
      .addCase(fetchAllUserGroups.fulfilled, (state, action) => {
        state.UserGroups = action.payload;
        state.FetchingUserGroups = false;
      })
      .addCase(fetchAllUserGroups.rejected, (state) => {
        state.FetchingUserGroups = false;
      })
      .addCase(fetchUserGroup.fulfilled, (state, action) => {
        state.UserGroup = action.payload;
      })
      .addCase(createUserGroup.pending, (state) => {
        state.CreatingUserGroup = true;
      })
      .addCase(createUserGroup.fulfilled, (state) => {
        toast.success("Group Created");
        state.CreatingUserGroup = false;
      })
      .addCase(createUserGroup.rejected, (state) => {
        state.CreatingUserGroup = false;
      })
      .addCase(deleteUserGroup.pending, (state) => {
        state.DeletingUserGroup = true;
      })
      .addCase(deleteUserGroup.fulfilled, (state) => {
        toast.success("Group Deleted");
        state.DeletingUserGroup = false;
      })
      .addCase(deleteUserGroup.rejected, (state) => {
        state.DeletingUserGroup = false;
      });
  },
});

export const fetchAllUserGroups = createAsyncThunk("UserGroups/fetchAllUserGroups", async () => {
  const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  return await usersGroupService.getAllUserGroups();
});

export const fetchUserGroup = createAsyncThunk("UserGroups/fetchUserGroup", async (groupID: number) => {
  const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  return await usersGroupService.getUserGroup(groupID);
});

export const deleteUserGroup = createAsyncThunk<void, number, { state: RootState }>(
  "UserGroups/DeleteUserGroup",
  async (userGroupID: number, thunkAPI) => {
    const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
    await usersGroupService.deleteUserGroup(userGroupID);
    thunkAPI.dispatch(fetchAllUserGroups());
  }
);

export const createUserGroup = createAsyncThunk<void, string, { state: RootState }>(
  "UserGroups/CreateUserGroup",
  async (groupName: string, thunkAPI) => {
    const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
    const userResponse = await usersGroupService.createUserGroup(groupName);
    thunkAPI.dispatch(fetchAllUserGroups());
    return userResponse;
  }
);

export const updateUserGroup = createAsyncThunk<void, UserGroupModel, { state: RootState }>(
  "UserGroups/UpdateUserGroup",
  async (userGroup: UserGroupModel, thunkAPI) => {
    const usersGroupService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
    await usersGroupService.updateUserGroup(userGroup);
    thunkAPI.dispatch(fetchAllUserGroups());
  }
);

export const { resetUserGroup } = userGroupSlice.actions;

export const selectUserGroup = (state: RootState) => {
  return state.userGroups.UserGroup;
};
export const selectUserGroups = (state: RootState) => {
  return state.userGroups.UserGroups;
};
export const selectFetchingUserGroups = (state: RootState) => {
  return state.userGroups.FetchingUserGroups;
};
export const selectCreatingUserGroup = (state: RootState) => {
  return state.userGroups.CreatingUserGroup;
};
export const selectDeletingUserGroup = (state: RootState) => {
  return state.userGroups.DeletingUserGroup;
};
export const selectUpdatingUserGroup = (state: RootState) => {
  return state.userGroups.UpdatingUserGroup;
};

export default userGroupSlice.reducer;
